import React, { useCallback, useEffect, useState } from "react";
import {
  Text,
  Button,
  NumberController,
  ReactSelectController,
  MultiCheckboxController,
  Toast,
  Icons,
} from "@pushpress/shared-components";
import { useForm, useWatch } from "react-hook-form";
import { ClubCheckinRules, UpdateClubCallback } from "./types";
import {
  appointmentCheckinOptions,
  classCheckinOptions,
  eventCheckinOptions,
} from "./utils";
import { useAnalytics } from "../../components/providers/AnalyticsProvider";
import { CommittedClubEvent } from "./CommittedClubEvents";

type EntityTypeOption = {
  label: string;
  value: string;
};

type SetupRulesPaneProps = {
  isClubSetup: boolean;
  hasLaunched: boolean;
  value: ClubCheckinRules | undefined;
  classTypes: EntityTypeOption[];
  appointmentTypes: EntityTypeOption[];
  setRules: React.Dispatch<React.SetStateAction<ClubCheckinRules>>;
  closeDrawer: (trackClose: boolean) => void;
  updateClub: UpdateClubCallback;
};

const SetupRulesPane = (props: SetupRulesPaneProps) => {
  const {
    value,
    hasLaunched,
    isClubSetup,
    classTypes,
    appointmentTypes,
    setRules,
    closeDrawer,
    updateClub,
  } = props;

  const [error, setError] = useState<string | null>(null);

  const { control } = useForm<ClubCheckinRules>({
    mode: "onTouched",
    defaultValues: value ?? {
      checkinCount: 12,
      checkinTypeLevel: classCheckinOptions[0],
      checkinTypes: classTypes.map((o) => o.value),
      appointmentTypeLevel: appointmentCheckinOptions[0],
      appointmentTypes: appointmentTypes.map((o) => o.value),
      eventTypes: eventCheckinOptions[0],
    },
  });

  const rules = useWatch({ control }) as ClubCheckinRules;

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (rules.checkinCount === null) {
      return setError("Required");
    }

    const text = rules.checkinCount.toString();
    const num = parseInt(text, 10);

    if (!text || !text.trim()) {
      setError("Required");
    } else if (!/^-?\d*$/.test(text)) {
      setError("Invalid number");
    } else if (num < 1) {
      setError("Minimum is 1");
    } else {
      setError(null);
    }
  }, [rules.checkinCount]);

  const filterOutNonNumericInput = useCallback(
    (event: React.FormEvent<HTMLDivElement>) => {
      const inputEvent = event.nativeEvent as InputEvent;

      if (inputEvent.data && !/^\d$/.test(inputEvent.data)) {
        event.preventDefault();
      }
    },
    [],
  );

  const save = useCallback(() => {
    if (error) return;

    trackEvent(CommittedClubEvent.RulesSaved, {
      app: "committed_club",
      live_now: isClubSetup,
      state: hasLaunched ? "launched" : "pre_launch",
      page: "rules_panel",
    });

    setRules(rules);
    closeDrawer(false);

    if (isClubSetup) {
      updateClub({
        criteriaTotal: rules.checkinCount,
        criteria: {
          classSelectionStrategy: rules.checkinTypeLevel.value,
          appointmentSelectionStrategy: rules.appointmentTypeLevel.value,
          includeEvents: ["BOTH", "EVENTS"].includes(rules.eventTypes.value),
          includeOpenGym: ["BOTH", "OPENGYMS"].includes(rules.eventTypes.value),
          selectedClassIds:
            rules.checkinTypeLevel.value === "SPECIFIC"
              ? rules.checkinTypes
              : null,
          selectedAppointmentTypeIds:
            rules.appointmentTypeLevel.value === "SPECIFIC"
              ? rules.appointmentTypes
              : null,
        },
      });
    }
  }, [
    error,
    isClubSetup,
    hasLaunched,
    rules,
    setRules,
    trackEvent,
    closeDrawer,
    updateClub,
  ]);

  return (
    <>
      {hasLaunched && (
        <>
          <Toast
            icon={Icons.Info}
            iconColor="success"
            message="If you change the rules, the new rules will be applied for next month and all future months but will not affect this month or previous months."
          />
          <div className="tw-h-4" />
        </>
      )}

      <Text variant="heading-5" color="black">
        How many check-ins per month to make it into your Committed Club?
      </Text>

      <div className="tw-h-3" />

      <NumberController
        hideLabel
        hideHelperText
        control={control}
        name="checkinCount"
        endOuterText="per month"
        inputWidth="64px"
        autoComplete="off"
        onBeforeInput={filterOutNonNumericInput}
        min={1}
        label={" "}
        helperText={" "}
        error={!!error}
      />

      <div className="tw-pb-6">
        <div className="tw-h-3">
          <Text
            variant="body-sm"
            color="wild-watermellon-600"
            className={error ? "tw-block tw-pb-1" : "tw-hidden"}
          >
            {error}
          </Text>
        </div>
        <Button text="Help me choose" buttonType="text" size="medium" />
      </div>

      <div className="tw-h-2" />

      <Text variant="heading-5" color="black">
        Which types of check-ins count?
      </Text>

      <div className="tw-h-3" />

      <ReactSelectController
        hideLabel
        label=""
        size="large"
        control={control}
        name="checkinTypeLevel"
        options={classCheckinOptions}
      />

      {rules.checkinTypeLevel.value === "SPECIFIC" && (
        <MultiCheckboxController
          hideLabel
          label=""
          name="checkinTypes"
          control={control}
          options={classTypes}
        />
      )}

      <ReactSelectController
        hideLabel
        label=""
        size="large"
        control={control}
        name="appointmentTypeLevel"
        options={appointmentCheckinOptions}
      />

      {rules.appointmentTypeLevel.value === "SPECIFIC" && (
        <MultiCheckboxController
          hideLabel
          label=""
          name="appointmentTypes"
          control={control}
          options={appointmentTypes}
        />
      )}

      <ReactSelectController
        hideLabel
        label=""
        size="large"
        control={control}
        name="eventTypes"
        options={eventCheckinOptions}
      />

      <Button
        fullWidth
        text="Save"
        buttonType="primary"
        size="large"
        onClick={save}
      />
    </>
  );
};

export default SetupRulesPane;
